import React, { useState, useEffect } from 'react';
import { ConsentStorage } from '../services/storage';
import { useLanguage } from '../contexts/LanguageContext';

export function ConsentBanner({ onAccept }) {
  const { t } = useLanguage();
  const [visible, setVisible] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [consent, setConsent] = useState({
    essential: true,
    analytics: true,
    marketing: false
  });

  useEffect(() => {
    const savedConsent = ConsentStorage.getConsent();
    if (!savedConsent) {
      setTimeout(() => setVisible(true), 800);
    }
  }, []);

  const updateGoogleConsent = (consentData) => {
    if (typeof window !== 'undefined' && window.gtag) {
      window.gtag('consent', 'update', {
        'analytics_storage': consentData.analytics ? 'granted' : 'denied',
        'ad_storage': consentData.marketing ? 'granted' : 'denied',
        'ad_user_data': consentData.marketing ? 'granted' : 'denied',
        'ad_personalization': consentData.marketing ? 'granted' : 'denied'
      });
    }
  };

  const handleAcceptAll = () => {
    const fullConsent = { essential: true, analytics: true, marketing: true };
    ConsentStorage.setConsent(fullConsent);
    updateGoogleConsent(fullConsent);
    setVisible(false);
    onAccept?.(fullConsent);
  };

  const handleEssentialOnly = () => {
    const minimalConsent = { essential: true, analytics: false, marketing: false };
    ConsentStorage.setConsent(minimalConsent);
    updateGoogleConsent(minimalConsent);
    setVisible(false);
    onAccept?.(minimalConsent);
  };

  const handleSavePreferences = () => {
    ConsentStorage.setConsent(consent);
    updateGoogleConsent(consent);
    setVisible(false);
    onAccept?.(consent);
  };

  if (!visible) return null;

  return (
    <div id="consent-banner" className={visible ? 'show' : ''}>
      <div className="consent-content">
        {!showDetails ? (
          <div className="consent-simple">
            <div className="consent-text">
              <h3>{t('cookies.title')}</h3>
              <p>
                {t('cookies.description')}{' '}
                <a href="#" onClick={(e) => { e.preventDefault(); setShowDetails(true); }}>
                  {t('cookies.moreInfo')}
                </a>
              </p>
            </div>
            <div className="consent-actions">
              <button className="consent-btn consent-btn-secondary" onClick={handleEssentialOnly}>
                {t('cookies.essentialOnly')}
              </button>
              <button className="consent-btn consent-btn-primary" onClick={handleAcceptAll}>
                {t('cookies.acceptAll')}
              </button>
            </div>
          </div>
        ) : (
          <div className="consent-details show">
            <div className="consent-header">
              <h3>{t('cookies.settingsTitle')}</h3>
              <button className="consent-close-btn" onClick={() => setShowDetails(false)}>×</button>
            </div>

            <div className="cookie-options">
              <div className="cookie-option disabled">
                <input type="checkbox" checked disabled />
                <div className="cookie-info">
                  <div className="cookie-title">{t('cookies.essentialCookies')}</div>
                  <div className="cookie-description">
                    {t('cookies.essentialDesc')}
                  </div>
                </div>
                <span className="cookie-badge">{t('cookies.required')}</span>
              </div>

              <div className="cookie-option">
                <input 
                  type="checkbox" 
                  checked={consent.analytics}
                  onChange={(e) => setConsent(prev => ({ ...prev, analytics: e.target.checked }))}
                />
                <div className="cookie-info">
                  <div className="cookie-title">{t('cookies.analyticsCookies')}</div>
                  <div className="cookie-description">
                    {t('cookies.analyticsDesc')}
                  </div>
                </div>
              </div>

              <div className="cookie-option">
                <input 
                  type="checkbox" 
                  checked={consent.marketing}
                  onChange={(e) => setConsent(prev => ({ ...prev, marketing: e.target.checked }))}
                />
                <div className="cookie-info">
                  <div className="cookie-title">{t('cookies.marketingCookies')}</div>
                  <div className="cookie-description">
                    {t('cookies.marketingDesc')}
                  </div>
                </div>
              </div>
            </div>

            <div className="consent-footer">
              <button className="consent-btn consent-btn-secondary" onClick={() => setShowDetails(false)}>
                {t('common.cancel')}
              </button>
              <button className="consent-btn consent-btn-primary" onClick={handleSavePreferences}>
                {t('cookies.saveSettings')}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default ConsentBanner;
